export type Purpose = "sale" | "rent" | "lease" | "parking";
export type PropertyType =
  | "house"
  | "apartment"
  | "villa"
  | "land"
  | "room"
  | "office"
  | "shop"
  | "commercial"
  | "warehouse";

export interface Property {
  id: string;
  title: string;
  type: PropertyType;
  purpose: Purpose;
  price: number;
  priceLabel: string;
  location: string;
  distanceKm: number;
  bedrooms?: number;
  bathrooms?: number;
  balcony?: number;
  areaSqft: number;
  highlights: string[];
  description: string;
  image: string;
  // Position in the faux-map viewport (%)
  x: number;
  y: number;
  /** Real coordinates when available (live listings). */
  lat?: number;
  lng?: number;
  createdAt?: string;
}

const img = (seed: string) => `https://images.unsplash.com/${seed}?auto=format&fit=crop&w=800&q=70`;

export const MOCK_PROPERTIES: Property[] = [
  {
    id: "p1",
    title: "Sunlit 3BHK Villa with Garden",
    type: "villa",
    purpose: "sale",
    price: 12500000,
    priceLabel: "₹1.25 Cr",
    location: "Anna Nagar, Chennai",
    distanceKm: 1.2,
    bedrooms: 3,
    bathrooms: 3,
    balcony: 2,
    areaSqft: 2100,
    highlights: ["Corner Plot", "Vaastu", "Gated Community"],
    description:
      "Premium villa with private garden, modular kitchen, and covered parking. Ready to move in.",
    image: img("photo-1600585154340-be6161a56a0c"),
    x: 32,
    y: 42,
  },
  {
    id: "p2",
    title: "2BHK Modern Apartment",
    type: "apartment",
    purpose: "rent",
    price: 28000,
    priceLabel: "₹28,000 / mo",
    location: "T. Nagar, Chennai",
    distanceKm: 2.4,
    bedrooms: 2,
    bathrooms: 2,
    balcony: 1,
    areaSqft: 1150,
    highlights: ["Semi-Furnished", "Lift", "24/7 Security"],
    description:
      "Bright and airy apartment on the 6th floor with balcony views and reserved parking.",
    image: img("photo-1560448204-e02f11c3d0e2"),
    x: 55,
    y: 30,
  },
  {
    id: "p3",
    title: "Commercial Land 4800 sqft",
    type: "land",
    purpose: "lease",
    price: 90000,
    priceLabel: "₹90,000 / mo",
    location: "OMR, Chennai",
    distanceKm: 5.1,
    areaSqft: 4800,
    highlights: ["Road Facing", "Clear Title", "IT Corridor"],
    description:
      "Prime commercial plot suitable for showroom, cafe, or office setup. Long lease available.",
    image: img("photo-1500382017468-9049fed747ef"),
    x: 70,
    y: 62,
  },
  {
    id: "p4",
    title: "Cozy Studio near Metro",
    type: "room",
    purpose: "rent",
    price: 12500,
    priceLabel: "₹12,500 / mo",
    location: "Guindy, Chennai",
    distanceKm: 3.7,
    bedrooms: 1,
    bathrooms: 1,
    areaSqft: 420,
    highlights: ["Metro 5 min", "Furnished", "Bachelors OK"],
    description: "Compact studio ideal for working professionals, fully furnished with wifi.",
    image: img("photo-1502672260266-1c1ef2d93688"),
    x: 42,
    y: 68,
  },
  {
    id: "p5",
    title: "Independent 4BHK House",
    type: "house",
    purpose: "sale",
    price: 21500000,
    priceLabel: "₹2.15 Cr",
    location: "Kilpauk, Chennai",
    distanceKm: 4.3,
    bedrooms: 4,
    bathrooms: 4,
    balcony: 3,
    areaSqft: 3200,
    highlights: ["North Facing", "Newly Built", "Solar Ready"],
    description: "Spacious home with double car parking, terrace garden and premium fittings.",
    image: img("photo-1568605114967-8130f3a36994"),
    x: 22,
    y: 22,
  },
  {
    id: "p6",
    title: "Boutique Shop on High Street",
    type: "shop",
    purpose: "rent",
    price: 65000,
    priceLabel: "₹65,000 / mo",
    location: "Pondy Bazaar, Chennai",
    distanceKm: 2.9,
    areaSqft: 620,
    highlights: ["High Footfall", "Glass Frontage", "Storage"],
    description: "Ground-floor shop on a busy shopping street, ideal for retail or cafe.",
    image: img("photo-1519567241046-7f570eee3ce6"),
    x: 62,
    y: 48,
  },
];

export const PROPERTY_TYPES: { value: PropertyType | "all"; label: string }[] = [
  { value: "all", label: "All" },
  { value: "house", label: "House" },
  { value: "apartment", label: "Apartment" },
  { value: "villa", label: "Villa" },
  { value: "land", label: "Land" },
  { value: "room", label: "Room" },
  { value: "office", label: "Office" },
  { value: "shop", label: "Shop" },
  { value: "commercial", label: "Commercial" },
  { value: "warehouse", label: "Warehouse" },
];

export const PURPOSES: { value: Purpose | "all"; label: string }[] = [
  { value: "all", label: "All" },
  { value: "sale", label: "Sale" },
  { value: "rent", label: "Rent" },
  { value: "lease", label: "Lease" },
  { value: "parking", label: "Parking" },
];

export type UserRole = "super_admin" | "admin" | "partner" | "customer" | "guest";

export const VERIFICATION_STATUSES = [
  "Not Started",
  "Mobile Verified",
  "Identity Submitted",
  "Identity Verified",
  "Selfie Submitted",
  "Video Verification Pending",
  "Video Verification Completed",
  "Property Verification Pending",
  "Property Verification Completed",
  "Agreement Pending",
  "Agreement Signed",
  "Approved",
  "Rejected",
  "Suspended",
] as const;
export type VerificationStatus = (typeof VERIFICATION_STATUSES)[number];

export const AGREEMENT_TYPES = [
  "Property Owner Declaration",
  "Land Owner Declaration",
  "House Rent Owner Declaration",
  "Broker Authorisation Declaration",
  "Buyer Disclaimer",
  "Tenant Verification Consent",
  "Contact Unlock Consent",
  "Platform Terms and Conditions",
] as const;

export const PARTNER_PLANS = [
  { name: "Free", price: "₹0", features: ["1 listing", "Basic visibility"] },
  { name: "Rural Basic", price: "₹299 / mo", features: ["5 listings", "Rural boost"] },
  { name: "City Basic", price: "₹799 / mo", features: ["15 listings", "City boost"] },
  { name: "Premium", price: "₹1,999 / mo", features: ["50 listings", "Featured tag"] },
  { name: "Builder / Agency", price: "Custom", features: ["Unlimited", "Team seats"] },
];

export const CUSTOMER_PLANS = [
  { name: "Free Browse", price: "₹0", features: ["Browse map", "Save 5 favourites"] },
  { name: "Contact Unlock", price: "₹49 / unlock", features: ["Pay per contact"] },
  { name: "Buyer / Investor", price: "₹499 / mo", features: ["Unlimited unlocks", "Priority"] },
];
